// One connection (institution) group on `#s01`: a header with the
// institution name, account count, and `ConnectionStatusPill`, then its
// account rows. Manual accounts render under a `Manual` pseudo-connection
// and are excluded from sync health (execution plan §4 rule 5). The design
// groups by connection, not by account class -- the class label lives in
// each row instead (§9 conflict #11).
import type { Account, CanonicalAccount, ConnectionStatus } from '@/api/hooks/useAccounts';
import { ConnectionStatusPill } from '@/components/ConnectionStatusPill';
import { AmountText } from '@/components/AmountText';
import { AccountRow } from './AccountRow';
import { computeNetWorthRollup } from './netWorthRollup';

export interface ConnectionGroupData {
  key: string;
  institutionName: string;
  status: ConnectionStatus;
  lastSyncedAt: string | null;
  accounts: Account[];
}

export type CanonicalConnectionGroupsResult =
  | { state: 'ready'; groups: ConnectionGroupData[] }
  | { state: 'inconsistent'; groups: [] };

export function groupAccountsByConnection(accounts: Account[]): ConnectionGroupData[] {
  const groups = new Map<string, ConnectionGroupData>();
  for (const account of accounts) {
    const isManual = account.source === 'manual';
    const key = isManual ? 'manual' : (account.item_id ?? account.institution_id ?? account.institution_name ?? 'unknown');
    const existing = groups.get(key);
    if (existing) {
      existing.accounts.push(account);
      // A connection is only as healthy as its least-healthy account.
      if (account.status !== 'synced' && account.status !== 'manual') existing.status = account.status;
      if (account.last_synced_at && (!existing.lastSyncedAt || account.last_synced_at > existing.lastSyncedAt)) {
        existing.lastSyncedAt = account.last_synced_at;
      }
      continue;
    }
    groups.set(key, {
      key,
      institutionName: isManual ? 'Manual' : (account.institution_name ?? 'Unknown institution'),
      status: isManual ? 'manual' : account.status,
      lastSyncedAt: account.last_synced_at,
      accounts: [account],
    });
  }
  return Array.from(groups.values());
}

export function groupCanonicalAccountsForPresentation(accounts: readonly CanonicalAccount[]): CanonicalConnectionGroupsResult {
  const seen = new Set<string>();
  for (const account of accounts) {
    if (account.canonical_account_id === null) continue;
    if (seen.has(account.canonical_account_id)) return { state: 'inconsistent', groups: [] };
    seen.add(account.canonical_account_id);
  }
  return { state: 'ready', groups: groupAccountsByConnection(Array.from(accounts)) };
}

export function ConnectionGroup({
  group,
  dense = false,
  className,
  aggregateDisplay = 'physical',
}: {
  group: ConnectionGroupData;
  dense?: boolean;
  className?: string;
  aggregateDisplay?: 'physical' | 'canonical_unavailable';
}) {
  // A per-connection subtotal, same rules as the page-level rollup: never a
  // fabricated number when every account in the group is unknown, and a
  // visibly-partial figure (never silently summed as complete) otherwise.
  const subtotal = aggregateDisplay === 'physical' ? computeNetWorthRollup(group.accounts) : null;

  return (
    <section className={className} data-testid="connection-group" data-connection-key={group.key}>
      <div className="flex items-center justify-between px-1 py-1.5 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">
          {group.institutionName} <span className="font-normal text-muted-foreground">· {group.accounts.length} account{group.accounts.length === 1 ? '' : 's'}</span>
        </span>
        {group.status !== 'manual' && (
          <ConnectionStatusPill status={group.status} lastSyncedAt={group.lastSyncedAt} errorCode={group.accounts.find((account) => account.status === group.status)?.status_detail} />
        )}
      </div>
      <div className="rounded-lg border border-border bg-card divide-y divide-border-strong/40">
        {group.accounts.map((account) => (
          <AccountRow key={account.account_id} account={account} dense={dense} />
        ))}
        {group.accounts.length > 1 && (
          <div
            className="flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-muted-foreground"
            data-testid="connection-group-subtotal"
          >
            <span>Subtotal</span>
            {aggregateDisplay === 'canonical_unavailable' ? (
              <span className="text-degraded-foreground">unavailable</span>
            ) : subtotal?.total === null ? (
              <span className="text-degraded-foreground">no balance yet</span>
            ) : (
              <AmountText value={String(subtotal?.total)} variant="balance" balanceStatus={subtotal?.shape === 'partial' ? 'stale' : 'ok'} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default ConnectionGroup;
